import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Chip, DsButton } from "../../ds";
import { Textarea } from "../ui/textarea";
import { RejectDialog } from "./RejectDialog";
import { STATUS_COLOR, dubaiTime } from "./QueueTable";
import { ItemMedia } from "./ItemMedia";
import { ItemDetailDialog } from "./ItemDetailDialog";
import { ArchiveDialog } from "./ArchiveDialog";
import { ProductionActions } from "./ProductionActions";

const STATUS_LABEL = { production_approved: "Approved for production" };

const DECIDABLE = ["waiting_visual_approval", "visual_rejected"];
const REJECTABLE = ["waiting_visual_approval", "approved_waiting_n8n", "visual_rejected", "waiting_for_video"];

export const isPastDue = (iso) => !!iso && new Date(iso).getTime() <= Date.now();

export const ArchivedLine = ({ item }) => (
  <p className="text-xs text-[color:var(--muted)]" title={item.archive_reason || "No reason given"} data-testid={`archived-info-${item.id}`}>
    Archived{item.archived_by ? ` by ${item.archived_by}` : ""}{item.archived_at ? ` · ${dubaiTime(item.archived_at)}` : ""}
    {item.archive_reason && <span className="block text-[color:var(--fg)]" data-testid={`archived-reason-${item.id}`}>Reason: {item.archive_reason}</span>}
  </p>
);

export const GalleryCard = ({ item, siblings = [], onChanged }) => {
  const [caption, setCaption] = useState(item.caption || "");
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const canDecide = DECIDABLE.includes(item.status);
  const pastDue = isPastDue(item.scheduled_at);

  const approve = async () => {
    setBusy(true);
    try {
      const body = caption !== (item.caption || "") ? { caption } : {};
      const { data } = await api.post(`/social/items/${encodeURIComponent(item.id)}/approve`, body);
      if (data.past_due) toast.warning("Approved. Scheduled time has passed; the publisher will post it at its next run.");
      else toast.success("Approved for publishing.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const archive = async (reason) => {
    setBusy(true);
    try {
      await api.post("/social/queue/archive", { ids: [item.id], reason: reason || undefined });
      toast.success("Archived.");
      setArchiveOpen(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/social/items/${encodeURIComponent(item.id)}/restore`);
      toast.success("Restored.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const prodCall = async (path, msg) => {
    setBusy(true);
    try {
      await api.post(`/social/items/${encodeURIComponent(item.id)}/${path}`);
      toast.success(msg);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const prodProps = {
    item, busy,
    onApprove: () => prodCall("approve-production", "Approved for production. The VPS will render the video."),
    onRetry: () => prodCall("retry-production", "Production queued again."),
    onReject: () => setRejectOpen(true),
  };

  const reject = async (note, applyToGroup) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/items/${encodeURIComponent(item.id)}/reject`, { note, apply_to_group: applyToGroup });
      toast.success(data.group
        ? `Rejected ${data.item_ids.length} channels. One regenerate task created for Social Lead.`
        : "Rejected. Regenerate task created for Social Lead.");
      setRejectOpen(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <article className="rounded-[8px] border border-[color:var(--line)] bg-[color:var(--panel)] overflow-hidden flex flex-col" data-testid={`gallery-card-${item.id}`}>
      <ItemMedia item={item} onOpen={() => setDetailOpen(true)} />
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-xs uppercase text-[color:var(--muted)]">{item.channel}{item.media_type === "video" ? " · video" : ""}</span>
          <Chip value={STATUS_LABEL[item.status] || item.status} color={STATUS_COLOR[item.status]} testId={`gallery-status-${item.id}`} />
        </div>
        <button type="button" className="text-left font-mono text-xs text-[color:var(--cobalt)] hover:underline w-fit" onClick={() => setDetailOpen(true)} data-testid={`gallery-open-detail-${item.id}`}>Open preview</button>
        <div className="font-mono text-xs text-[color:var(--fg)]">{dubaiTime(item.scheduled_at)} · Dubai</div>
        {siblings.length > 0 && (
          <div className="text-xs text-[color:var(--muted)]" data-testid={`gallery-same-image-${item.id}`}>
            Same {item.media_type === "video" ? "video" : "image"} as: {siblings.map((s) => s.channel).join(", ")}
          </div>
        )}
        {pastDue && canDecide && (
          <div className="flex items-center gap-1 text-xs text-[color:var(--warn)]" data-testid={`gallery-pastdue-${item.id}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Scheduled time passed — will post at the next run.
          </div>
        )}
        <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} disabled={!canDecide} className="text-sm" data-testid={`gallery-caption-${item.id}`} />
        {item.visual_note && (
          <p className="text-xs text-[color:var(--bad)]" data-testid={`gallery-note-${item.id}`}>Last note: {item.visual_note}</p>
        )}
        {canDecide && (
          <div className="flex gap-2 mt-auto">
            <DsButton variant="go" className="flex-1" disabled={busy} onClick={approve} data-testid={`gallery-approve-${item.id}`}>Approve</DsButton>
            <DsButton variant="no" className="flex-1" disabled={busy || item.status === "visual_rejected"} onClick={() => setRejectOpen(true)} data-testid={`gallery-reject-${item.id}`}>Reject</DsButton>
          </div>
        )}
        <ProductionActions {...prodProps} />
        {item.status === "archived" && <ArchivedLine item={item} />}
        {item.status === "archived" ? (
          <DsButton className="mt-auto" disabled={busy} onClick={restore} data-testid={`gallery-restore-${item.id}`}>Restore</DsButton>
        ) : item.status !== "published" && (
          <DsButton variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => setArchiveOpen(true)} data-testid={`gallery-archive-${item.id}`}>Archive</DsButton>
        )}
      </div>
      {detailOpen && (
        <ItemDetailDialog item={item} open={detailOpen} onOpenChange={setDetailOpen}>
          <ProductionActions {...prodProps} suffix="-detail" />
        </ItemDetailDialog>
      )}
      {archiveOpen && <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} count={1} onConfirm={archive} busy={busy} testId={`archive-item-dialog`} />}
      {rejectOpen && <RejectDialog item={item} siblings={siblings.filter((s) => REJECTABLE.includes(s.status))} open={rejectOpen} onOpenChange={setRejectOpen} onConfirm={reject} busy={busy} />}
    </article>
  );
};
